import React, {useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {enqueueSnackbar} from 'notistack'
import {
    CreatePaymentPlanPayload,
    grossAmountCents,
    InstallmentDraft,
    isOverdue,
    PaymentInstallment,
    PaymentPlan,
    splitEvenly,
    toCents,
} from '../../domain/entities/PaymentPlan.ts'
import {ConfirmDialog} from '../dialogs/ConfirmDialog.tsx'
import {Dialog} from '../Dialog.tsx'
import {Button} from '../Button.tsx'
import {Input} from '../Input.tsx'
import IconSpinner from '../../assets/icons/spinner.svg?react'
import IconTrash from '../../assets/icons/trash.svg?react'
import IconCopy from '../../assets/icons/copy.svg?react'
import IconLink from '../../assets/icons/copy-link.svg?react'
import IconChevronDown from '../../assets/icons/chevron-down.svg?react'

// Packs sold through Stripe payment links, one link per negotiated installment.
// They live in the agency's "Paquetes de TripBooks" section: a plan with no
// first payment yet is a pending row there, and once the first installment is
// paid the backend creates the pack (plan.packageId) and the plan hangs off it.
// Data comes from usePaymentPlans.

// Mirrors MAX_INSTALLMENTS in colab-api's PaymentPlanService.
const MAX_INSTALLMENTS = 24

const today = () => new Date().toISOString().split('T')[0]

interface NewPaymentPlanFormProps {
    currency: string
    // Pre-filled VAT (the sales rep can change it per plan).
    defaultVatRate: number
    agencyPages: number
    formatMoney: (amount: number) => string
    onSubmit: (payload: CreatePaymentPlanPayload) => Promise<void>
    onCancel: () => void
}

export const NewPaymentPlanForm: React.FC<NewPaymentPlanFormProps> = ({currency, defaultVatRate, agencyPages, formatMoney, onSubmit, onCancel}) => {
    const {t} = useTranslation('dashboard')
    const [date, setDate] = useState(today)
    const [quantity, setQuantity] = useState('')
    const [unitPrice, setUnitPrice] = useState('')
    const [pages, setPages] = useState('')
    const [vatPct, setVatPct] = useState(() => String(Math.round(defaultVatRate * 10000) / 100))
    const [installments, setInstallments] = useState<InstallmentsState>(() => freshInstallments(1))
    const [isSaving, setIsSaving] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const parsedQuantity = parseInt(quantity, 10)
    const parsedUnitPrice = parseFloat(unitPrice)
    const parsedVat = parseFloat(vatPct)
    const vatValid = !isNaN(parsedVat) && parsedVat >= 0 && parsedVat <= 100
    const targetCents = parsedQuantity > 0 && parsedUnitPrice > 0 && vatValid
        ? grossAmountCents(parsedUnitPrice, parsedQuantity, parsedVat / 100)
        : 0
    const drafts = toDrafts(installments, targetCents)
    const canSubmit = targetCents > 0 && !!date && draftsMatch(drafts, targetCents)

    const handleSubmit = () => {
        if (!canSubmit || isSaving) return
        const parsedPages = parseInt(pages, 10)
        setIsSaving(true)
        setError(null)
        onSubmit({
            unitPrice: parsedUnitPrice,
            quantity: parsedQuantity,
            date: new Date(date).getTime(),
            numberOfPagesIncluded: parsedPages > 0 ? parsedPages : undefined,
            vatRate: parsedVat / 100,
            currency: currency.toLowerCase(),
            installments: drafts,
        })
            .catch((err: Error) => setError(err.message))
            .finally(() => setIsSaving(false))
    }

    return (
        <div className="flex flex-col gap-4">
            <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
                <Field label={t('packageDate')}>
                    <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                           className="h-10 border border-neutralBlack-200 rounded-lg px-3 text-sm bg-white focus:outline-none focus:border-neutralBlack-900"/>
                </Field>
                <Field label={t('packageQuantity')}>
                    <Input id="planQuantity" type="number" min="1" invalid={false} placeholder="50"
                           value={quantity} onChange={(e) => setQuantity(e.target.value)} className="h-10"/>
                </Field>
                <Field label={t('paymentPlans.unitPriceNet')}>
                    <Input id="planUnitPrice" type="number" min="0" step="0.01" invalid={false} placeholder="12.00"
                           value={unitPrice} onChange={(e) => setUnitPrice(e.target.value)} className="h-10"/>
                </Field>
                <Field label={t('paymentPlans.vatPct')}>
                    <Input id="planVat" type="number" min="0" max="100" step="0.01" invalid={!vatValid && vatPct !== ''}
                           value={vatPct} onChange={(e) => setVatPct(e.target.value)} className="h-10"/>
                </Field>
                <Field label={t('packagePages')}>
                    <Input id="planPages" type="number" min="1" invalid={false} placeholder={String(agencyPages)}
                           value={pages} onChange={(e) => setPages(e.target.value)} className="h-10"/>
                </Field>
            </div>

            {targetCents > 0 && (
                <div className="text-[13px] text-neutralBlack-500 flex flex-wrap gap-x-4 gap-y-1">
                    <span>{t('paymentPlans.netTotal')}: <span className="font-medium text-neutralBlack-900">{formatMoney(parsedUnitPrice * parsedQuantity)}</span></span>
                    <span>{t('paymentPlans.grossTotal')}: <span className="font-semibold text-neutralBlack-900">{formatMoney(targetCents / 100)}</span></span>
                </div>
            )}

            <InstallmentsEditor state={installments} onChange={setInstallments} targetCents={targetCents} formatMoney={formatMoney}/>

            {error && <ErrorBox message={error}/>}

            <div className="flex gap-2">
                <Button variant="primary" size="small" className="max-h-[32px]" disabled={isSaving || !canSubmit} onClick={handleSubmit}>
                    {isSaving ? <IconSpinner className="size-4"/> : t('paymentPlans.createLinks')}
                </Button>
                <Button variant="secondary" size="small" className="max-h-[32px]" disabled={isSaving} onClick={onCancel}>
                    {t('cancel')}
                </Button>
            </div>
        </div>
    )
}

// The pill doubles as the toggle that shows the plan's installments.
export const PaymentPlanToggle: React.FC<{plan: PaymentPlan; expanded: boolean; onToggle: () => void}> = ({plan, expanded, onToggle}) => {
    const {t} = useTranslation('dashboard')
    const live = plan.installments.filter((i) => i.status !== 'CANCELED')
    const paidCount = live.filter((i) => i.status === 'PAID').length
    const overdue = live.some((i) => isOverdue(i))
    const [label, classes] = plan.status === 'PAID'
        ? [t('paymentPlans.status.PAID'), 'bg-success/10 text-success hover:bg-success/20']
        : overdue
            ? [t('paymentPlans.status.OVERDUE'), 'bg-danger/10 text-danger hover:bg-danger/20']
            : plan.status === 'PARTIAL'
                ? [t('paymentPlans.status.PARTIAL', {paid: paidCount, total: live.length}), 'bg-warning/10 text-warning hover:bg-warning/20']
                : [t('paymentPlans.status.PENDING'), 'bg-orange/10 text-orange hover:bg-orange/20']
    return (
        <button
            type="button"
            aria-expanded={expanded}
            title={t('paymentPlans.showInstallments')}
            onClick={onToggle}
            className={`inline-flex items-center gap-1 text-[11.5px] font-medium rounded-full pl-2.5 pr-2 py-0.5 whitespace-nowrap transition-colors ${classes}`}
        >
            {label}
            <IconChevronDown className={`size-3 ${expanded ? 'rotate-180' : ''}`} aria-hidden/>
        </button>
    )
}

interface PaymentPlanDetailsProps {
    plan: PaymentPlan
    formatMoney: (amount: number) => string
    onRenegotiate: (installments: InstallmentDraft[]) => Promise<void>
    onCancel: () => Promise<void>
}

// The plan's installments with their links, plus renegotiate / cancel.
export const PaymentPlanDetails: React.FC<PaymentPlanDetailsProps> = ({plan, formatMoney, onRenegotiate, onCancel}) => {
    const {t} = useTranslation('dashboard')
    const [isEditing, setIsEditing] = useState(false)
    const [installments, setInstallments] = useState<InstallmentsState>(() => freshInstallments(1))
    const [isSaving, setIsSaving] = useState(false)
    const [isCancelling, setIsCancelling] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const confirmCancelDialog = useRef<React.ElementRef<typeof Dialog<boolean>>>(null)

    const live = plan.installments.filter((i) => i.status !== 'CANCELED').sort((a, b) => a.dueDate - b.dueDate)
    const targetCents = remainingCents(plan)
    const drafts = toDrafts(installments, targetCents)

    const startEdit = () => {
        setInstallments({
            amountsTouched: true,
            rows: live.filter((i) => i.status === 'PENDING').map((i) => ({
                key: i.id,
                dueDate: new Date(i.dueDate).toISOString().split('T')[0],
                amount: i.amount.toFixed(2),
            })),
        })
        setError(null)
        setIsEditing(true)
    }

    const handleSave = () => {
        if (!draftsMatch(drafts, targetCents) || isSaving) return
        setIsSaving(true)
        setError(null)
        onRenegotiate(drafts)
            .then(() => setIsEditing(false))
            .catch((err: Error) => setError(err.message))
            .finally(() => setIsSaving(false))
    }

    // ConfirmDialog closes itself as soon as onConfirm returns truthy.
    const confirmCancel = () => {
        setIsCancelling(true)
        setError(null)
        onCancel()
            .catch((err: Error) => {
                setError(err.message)
                setIsCancelling(false)
            })
        return true
    }

    const copyLink = (url: string) => {
        navigator.clipboard?.writeText(url)
            .then(() => enqueueSnackbar(t('paymentPlans.linkCopied'), {variant: 'success'}))
            .catch(() => enqueueSnackbar(t('paymentPlans.linkCopyError'), {variant: 'error'}))
    }

    return (
        <div className="flex flex-col gap-3 bg-neutralBlack-50 rounded-xl px-4 py-3">
            <div className="flex items-center gap-3 flex-wrap text-xs text-neutralBlack-500">
                <span>
                    {t('paymentPlans.planTotals', {
                        gross: formatMoney(plan.grossAmount),
                        vat: Math.round(plan.vatRate * 10000) / 100,
                    })}
                </span>
                <span className="flex-1"/>
                {!isEditing && plan.status !== 'PAID' && (
                    <Button variant="secondary" size="small" className="max-h-[30px]" onClick={startEdit}>
                        {t('paymentPlans.renegotiate')}
                    </Button>
                )}
                {!isEditing && plan.status === 'PENDING' && (
                    <Button variant="secondary" size="small" className="max-h-[30px]" disabled={isCancelling}
                            onClick={() => confirmCancelDialog.current?.open()}>
                        {isCancelling ? <IconSpinner className="size-3"/> : t('paymentPlans.cancelPlan')}
                    </Button>
                )}
            </div>

            {isEditing ? (
                <div className="flex flex-col gap-3">
                    {live.filter((i) => i.status === 'PAID').map((i) => (
                        <InstallmentRow key={i.id} installment={i} number={live.indexOf(i) + 1} formatMoney={formatMoney} onCopy={copyLink}/>
                    ))}
                    <span className="text-[13px] text-neutralBlack-500">
                        {t('paymentPlans.remainingToCollect')}: <span className="font-semibold text-neutralBlack-900">{formatMoney(targetCents / 100)}</span>
                    </span>
                    <InstallmentsEditor state={installments} onChange={setInstallments} targetCents={targetCents} formatMoney={formatMoney}/>
                    <div className="flex gap-2">
                        <Button variant="primary" size="small" className="max-h-[32px]"
                                disabled={isSaving || !draftsMatch(drafts, targetCents)} onClick={handleSave}>
                            {isSaving ? <IconSpinner className="size-4"/> : t('paymentPlans.saveInstallments')}
                        </Button>
                        <Button variant="secondary" size="small" className="max-h-[32px]" disabled={isSaving} onClick={() => setIsEditing(false)}>
                            {t('cancel')}
                        </Button>
                    </div>
                </div>
            ) : (
                <div className="flex flex-col">
                    {live.map((i, index) => (
                        <InstallmentRow key={i.id} installment={i} number={index + 1} formatMoney={formatMoney} onCopy={copyLink}/>
                    ))}
                </div>
            )}

            {error && <ErrorBox message={error}/>}

            <ConfirmDialog
                ref={confirmCancelDialog}
                onConfirm={confirmCancel}
                title={t('paymentPlans.cancelConfirmTitle')}
                message={t('paymentPlans.cancelConfirmMessage')}
                confirmLabel={t('paymentPlans.cancelPlan')}
                cancelLabel={t('cancel')}
            />
        </div>
    )
}

const InstallmentRow: React.FC<{
    installment: PaymentInstallment
    number: number
    formatMoney: (amount: number) => string
    onCopy: (url: string) => void
}> = ({installment, number, formatMoney, onCopy}) => {
    const {t} = useTranslation('dashboard')
    const overdue = isOverdue(installment)
    return (
        <div className="flex items-center gap-3 py-2 border-b border-neutralBlack-200 last:border-0 text-[13px] flex-wrap">
            <span className="w-[72px] flex-none text-neutralBlack-500">{t('paymentPlans.installmentN', {n: number})}</span>
            <span className="w-[88px] flex-none text-neutralBlack-700">{formatDay(installment.dueDate)}</span>
            <span className="w-[110px] flex-none text-right font-medium text-neutralBlack-900">{formatMoney(installment.amount)}</span>
            {installment.status === 'PAID' ? (
                <span className="text-xs font-medium rounded-full px-2.5 py-0.5 bg-success/10 text-success">
                    {t('paymentPlans.paidOn', {date: installment.paidAt ? formatDay(installment.paidAt) : '—'})}
                </span>
            ) : overdue ? (
                <span className="text-xs font-medium rounded-full px-2.5 py-0.5 bg-danger/10 text-danger">{t('paymentPlans.status.OVERDUE')}</span>
            ) : (
                <span className="text-xs font-medium rounded-full px-2.5 py-0.5 bg-neutralBlack-200 text-neutralBlack-700">{t('paymentPlans.status.PENDING_INSTALLMENT')}</span>
            )}
            <span className="flex-1"/>
            {installment.status === 'PENDING' && installment.paymentUrl && (
                <div className="flex items-center gap-1">
                    <button
                        type="button"
                        onClick={() => onCopy(installment.paymentUrl!)}
                        className="h-[28px] px-2.5 flex items-center gap-1.5 rounded-full text-xs font-medium text-neutralBlack-700 hover:bg-neutralBlack-200 hover:text-neutralBlack-900"
                    >
                        <IconCopy className="size-3.5" aria-hidden/>
                        {t('paymentPlans.copyLink')}
                    </button>
                    <a
                        href={installment.paymentUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={t('paymentPlans.openLink')}
                        aria-label={t('paymentPlans.openLink')}
                        className="w-[28px] h-[28px] flex items-center justify-center rounded-full text-neutralBlack-500 hover:bg-neutralBlack-200 hover:text-neutralBlack-900"
                    >
                        <IconLink className="size-3.5" aria-hidden/>
                    </a>
                </div>
            )}
        </div>
    )
}

type InstallmentRowState = {key: string; dueDate: string; amount: string}

// While amountsTouched is false the amounts aren't the typed values but an even
// split of the target, so they follow price/quantity/VAT and the number of rows.
type InstallmentsState = {amountsTouched: boolean; rows: InstallmentRowState[]}

let rowKeySeq = 0
const newRow = (dueDate: string = today()): InstallmentRowState => ({key: `row-${++rowKeySeq}`, dueDate, amount: ''})
const freshInstallments = (count: number): InstallmentsState => ({
    amountsTouched: false,
    rows: Array.from({length: count}, () => newRow()),
})

function rowCents(state: InstallmentsState, targetCents: number): number[] {
    return state.amountsTouched
        ? state.rows.map((r) => toCents(parseFloat(r.amount) || 0))
        : splitEvenly(targetCents, state.rows.length)
}

function toDrafts(state: InstallmentsState, targetCents: number): InstallmentDraft[] {
    const cents = rowCents(state, targetCents)
    return state.rows.map((r, i) => ({amount: cents[i] / 100, dueDate: r.dueDate ? new Date(r.dueDate).getTime() : 0}))
}

function draftsMatch(drafts: InstallmentDraft[], targetCents: number): boolean {
    return targetCents > 0
        && drafts.length > 0
        && drafts.every((d) => d.amount > 0 && d.dueDate > 0)
        && drafts.reduce((acc, d) => acc + toCents(d.amount), 0) === targetCents
}

function remainingCents(plan: PaymentPlan): number {
    const paid = plan.installments.filter((i) => i.status === 'PAID').reduce((acc, i) => acc + toCents(i.amount), 0)
    return toCents(plan.grossAmount) - paid
}

const InstallmentsEditor: React.FC<{
    state: InstallmentsState
    onChange: (state: InstallmentsState) => void
    targetCents: number
    formatMoney: (amount: number) => string
}> = ({state, onChange, targetCents, formatMoney}) => {
    const {t} = useTranslation('dashboard')
    const cents = rowCents(state, targetCents)
    const diffCents = targetCents - cents.reduce((a, b) => a + b, 0)

    const updateRow = (index: number, patch: Partial<InstallmentRowState>) => {
        // The first manual amount freezes the even split as typed values.
        const rows = state.rows.map((r, i) => ({
            ...r,
            amount: state.amountsTouched ? r.amount : (cents[i] / 100).toFixed(2),
            ...(i === index ? patch : {}),
        }))
        onChange({amountsTouched: state.amountsTouched || patch.amount !== undefined, rows})
    }

    const addRow = () => {
        const last = state.rows[state.rows.length - 1]
        onChange({...state, rows: [...state.rows, newRow(nextMonth(last?.dueDate))]})
    }

    const removeRow = (index: number) => onChange({...state, rows: state.rows.filter((_, i) => i !== index)})

    return (
        <div className="flex flex-col gap-2">
            <span className="text-[12.5px] text-neutralBlack-600">{t('paymentPlans.installments')}</span>
            {state.rows.map((row, index) => (
                <div key={row.key} className="flex items-center gap-2 flex-wrap">
                    <span className="w-[72px] flex-none text-[13px] text-neutralBlack-500">{t('paymentPlans.installmentN', {n: index + 1})}</span>
                    <input
                        type="date"
                        aria-label={t('paymentPlans.dueDateOf', {n: index + 1})}
                        value={row.dueDate}
                        onChange={(e) => updateRow(index, {dueDate: e.target.value})}
                        className="h-9 border border-neutralBlack-200 rounded-lg px-2 text-[13px] bg-white focus:outline-none focus:border-neutralBlack-900"
                    />
                    <Input
                        id={`installment-amount-${row.key}`}
                        aria-label={t('paymentPlans.amountOf', {n: index + 1})}
                        type="number" min="0" step="0.01" invalid={false}
                        value={state.amountsTouched ? row.amount : (cents[index] / 100).toFixed(2)}
                        onChange={(e) => updateRow(index, {amount: e.target.value})}
                        className="h-9 w-[140px]"
                    />
                    {state.rows.length > 1 && (
                        <button
                            type="button"
                            title={t('paymentPlans.removeInstallment')}
                            aria-label={t('paymentPlans.removeInstallment')}
                            onClick={() => removeRow(index)}
                            className="w-[28px] h-[28px] flex-none flex items-center justify-center rounded-full text-neutralBlack-400 hover:bg-danger/10 hover:text-danger"
                        >
                            <IconTrash className="size-3"/>
                        </button>
                    )}
                </div>
            ))}
            <div className="flex items-center gap-3 flex-wrap">
                <Button variant="secondary" size="small" className="max-h-[30px]" disabled={state.rows.length >= MAX_INSTALLMENTS} onClick={addRow}>
                    + {t('paymentPlans.addInstallment')}
                </Button>
                {state.amountsTouched && (
                    <Button variant="secondary" size="small" className="max-h-[30px]" onClick={() => onChange({...state, amountsTouched: false})}>
                        {t('paymentPlans.splitEvenly')}
                    </Button>
                )}
                {targetCents > 0 && (
                    <span role="status" className={`text-[13px] ${diffCents === 0 ? 'text-success' : 'text-danger'}`}>
                        {diffCents === 0
                            ? t('paymentPlans.sumMatches', {total: formatMoney(targetCents / 100)})
                            : diffCents > 0
                                ? t('paymentPlans.sumMissing', {amount: formatMoney(diffCents / 100)})
                                : t('paymentPlans.sumExceeds', {amount: formatMoney(-diffCents / 100)})}
                    </span>
                )}
            </div>
        </div>
    )
}

const ErrorBox: React.FC<{message: string}> = ({message}) => (
    <div role="alert" className="text-sm text-danger bg-danger/10 rounded-xl px-3.5 py-2.5">{message}</div>
)

const Field: React.FC<{label: string; children: React.ReactNode}> = ({label, children}) => (
    <label className="flex flex-col gap-1.5">
        <span className="text-[12.5px] text-neutralBlack-600">{label}</span>
        {children}
    </label>
)

function nextMonth(isoDate?: string): string {
    const base = isoDate ? new Date(isoDate) : new Date()
    base.setUTCMonth(base.getUTCMonth() + 1)
    return base.toISOString().split('T')[0]
}

function formatDay(timestamp: number): string {
    return new Intl.DateTimeFormat('es-ES', {day: '2-digit', month: '2-digit', year: 'numeric'}).format(new Date(timestamp))
}
